import { useEffect, useRef, useState } from "react";
import { toBlob } from "html-to-image";
import { ArrowDownToLine, Check, Link2, X } from "lucide-react";
import Button from "./Button";
import Logo from "./Logo";
import IosSpinner from "./IosSpinner";
import DiscordIcon from "../icons/DiscordIcon";
import styles from "./ShareModal.module.css";
import { useLanguage } from "../LanguageContext";
import { useAuth } from "../AuthContext";
import { referralLink } from "../useReferrals";

function XIcon({ size = 15 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// navigator.clipboard needs a secure context; the textarea fallback covers the rest
async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  area.remove();
}

// Phones get the share sheet ("Save Image" on iOS): iOS Safari ignores a scripted
// download, and a long press on a data URL isn't something traders will find
const prefersShareSheet = () =>
  typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const FEEDBACK_MS = 2000;
const OVERLAY_IMAGES = ["/none.png", "/bull.png", "/bear.png"];

// html-to-image re-fetches every <img> while capturing, and the large overlay PNGs
// often weren't back in time, leaving a blank background. Inlining them as data URLs
// up front means the capture has nothing left to fetch.
const inlinedOverlays = {};
function inlineImage(src) {
  inlinedOverlays[src] ??= fetch(src)
    .then((res) => res.blob())
    .then((blob) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    }))
    .catch((err) => {
      delete inlinedOverlays[src];
      throw err;
    });
  return inlinedOverlays[src];
}

export default function ShareModal({ rank, propProfit, usdcPayouts, onClose }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const referralCode = user?.referralCode ? referralLink(user.referralCode) : "";
  const [customText, setCustomText] = useState(
    `${t("share_default_text").replace("{rank}", rank)}\n${t("share_text_line2")}`,
  );
  const [overlay, setOverlay] = useState(0);
  const [overlaySrcs, setOverlaySrcs] = useState(OVERLAY_IMAGES);
  // The overlay PNGs are large; the card shows a spinner until the chosen one has loaded
  const [overlayLoaded, setOverlayLoaded] = useState(false);
  const cardRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all(OVERLAY_IMAGES.map(inlineImage))
      .then((srcs) => { if (!cancelled) setOverlaySrcs(srcs); })
      .catch((err) => console.error("Failed to preload share overlays:", err));
    return () => { cancelled = true; };
  }, []);
  const [saving, setSaving] = useState(false);
  // Which action just succeeded ("link" | "discord"), or failed ("error"), for a moment
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [feedback]);

  // The default text ends "…with Propfunding.com"; put the referral link there, or on its own line if it was edited out
  const shareText = !referralCode
    ? customText
    : /propfunding\.com(?!\/)/i.test(customText)
      ? customText.replace(/propfunding\.com(?!\/)/i, referralCode)
      : `${customText}\n${referralCode}`;

  // Renders the card preview (overlay included) to a PNG at 2x
  const renderCard = async () => {
    // Make sure the overlay is inlined and decoded before it's drawn
    const img = overlayRef.current;
    const inlined = await inlineImage(OVERLAY_IMAGES[overlay]);
    if (img && img.src !== inlined) img.src = inlined;
    await img?.decode?.().catch(() => {});
    // The spinner may still be up while the overlay loads; it never belongs in the image
    return toBlob(cardRef.current, {
      pixelRatio: 2,
      filter: (node) => !node.classList?.contains(styles.cardLoader),
    });
  };

  // The card is rendered ahead of the tap: Safari only opens the share sheet while the
  // tap is still fresh, and rendering takes longer than it allows. Safari also draws
  // images late on a first capture, so the first pass is a throwaway.
  const prerendered = useRef(null);
  const cardKey = `${overlay}|${overlaySrcs[overlay]}|${rank}|${propProfit}|${usdcPayouts}|${referralCode}`;
  useEffect(() => {
    let cancelled = false;
    prerendered.current = null;
    if (!cardRef.current) return undefined;
    renderCard()
      .then(() => renderCard())
      .then((blob) => { if (!cancelled && blob) prerendered.current = { key: cardKey, blob }; })
      .catch((err) => console.error("Failed to prerender share image:", err));
    return () => { cancelled = true; };
    // renderCard reads the same values cardKey is built from
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardKey]);

  const saveImage = async () => {
    if (saving || !cardRef.current) return;
    const filename = `propfunding-leaderboard-${rank}.png`;
    setSaving(true);
    try {
      const ready = prerendered.current?.key === cardKey ? prerendered.current.blob : null;
      const blob = ready || (await renderCard());
      if (!blob) throw new Error("Empty image");
      const file = new File([blob], filename, { type: "image/png" });
      if (prefersShareSheet() && navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          return;
        } catch (err) {
          // Cancelling the sheet is fine; anything else (e.g. the tap went stale) falls back to a download
          if (err.name === "AbortError") return;
        }
      }
      downloadBlob(blob, filename);
    } catch (err) {
      console.error("Failed to save share image:", err);
      setFeedback("error");
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text, kind) => {
    try {
      await copyText(text);
      setFeedback(kind);
    } catch (err) {
      console.error("Copy failed:", err);
      setFeedback("error");
    }
  };

  const shareOnX = () => {
    window.open(`https://x.com/intent/post?text=${encodeURIComponent(shareText)}`, "_blank", "noopener,noreferrer");
  };

  // Discord has no share URL, so the post is copied for the trader to paste
  const shareOnDiscord = () => copy(shareText, "discord");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Share your leaderboard position"
      >
        <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        <span className={styles.sheetHandle} aria-hidden="true" />
        <p className={styles.modalTitle}>{t("share_modal_title")}</p>
        {/* ── Left: share card preview ── */}
        <div className={styles.card} ref={cardRef}>
          <div className={styles.cardLogoWrap}>
            <Logo className={styles.cardLogo} color="var(--sage-400)" />
          </div>
         
            <img
              ref={overlayRef}
              src={overlaySrcs[overlay]}
              className={styles.bullSvg}
              alt=""
              aria-hidden="true"
              onLoad={() => setOverlayLoaded(true)}
              onError={() => setOverlayLoaded(true)}
            />
            {!overlayLoaded && (
              <div className={styles.cardLoader} role="status" aria-label={t("share_loading")}>
                <IosSpinner size={40} />
              </div>
            )}
         
          <div className={`${styles.cardStats}${overlay === 2 ? ` ${styles.bearAccent}` : ""}`}>
            <p className={styles.cardPositionLabel}>
              {t("share_position_label").split("\n").map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </p>
            <strong className={styles.cardRank}>#{rank}</strong>
            <dl className={styles.cardMetrics}>
              <div className={styles.propProfit}>
                <dt>{t("share_prop_profit")}</dt>
                <dd>{propProfit} PRP</dd>
              </div>
              <div>
                <dt>{t("share_usdc_payouts")}</dt>
                <dd>{usdcPayouts}</dd>
              </div>
              <div>
                <dt>{t("share_referral_code")}</dt>
                <dd className={styles.cardRefUrl}>{referralCode}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* ── Right: controls ── */}
        <div className={styles.controls}>
          

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="share-referral">
              {t("share_field_referral")}
            </label>
            <input
              id="share-referral"
              className={styles.fieldInput}
              value={referralCode}
              readOnly
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="share-text">
              {t("share_field_text")}
            </label>
            <textarea
              id="share-text"
              className={styles.fieldTextarea}
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              rows={3}
            />
          </div>

          <div className={styles.field}>
            <p className={styles.fieldLabel}>{t("share_overlay")}</p>
            <div className={styles.overlayRow}>
              {[
                { key: "none", icon: "/none.svg", labelKey: "share_overlay_none" },
                { key: "bull", icon: "/bull-icon.svg", labelKey: "share_overlay_bull" },
                { key: "bear", icon: "/bear-icon.svg", labelKey: "share_overlay_bear" },
              ].map(({ key, icon, labelKey }, i) => (
                <button
                  key={key}
                  type="button"
                  className={`${styles.overlayThumb} ${overlay === i ? styles.overlayThumbActive : ""}`}
                  onClick={() => {
                    if (i === overlay) return;
                    setOverlayLoaded(false);
                    setOverlay(i);
                  }}
                  aria-pressed={overlay === i}
                  aria-label={t(labelKey)}
                >
                  <img src={icon} className={styles.overlayPreview} alt="" aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>

          <div className={styles.actions}>
            <div className={styles.actionBtns}>
              <Button
                icon={<ArrowDownToLine size={20} />}
                iconPosition="left"
                className={styles.actionBtn}
                onClick={saveImage}
                disabled={saving}
              >
                {saving ? t("share_saving") : t("share_save")}
              </Button>
              <Button
                icon={feedback === "link" ? <Check size={20} /> : <Link2 size={20} />}
                iconPosition="left"
                className={styles.actionBtn}
                onClick={() => copy(shareText, "link")}
                disabled={!referralCode}
              >
                {feedback === "link" ? t("ref_copied_btn") : t("share_copy_link")}
              </Button>
            </div>
            <div className={styles.shareRow}>
              <span className={styles.shareOnLabel}>{t("share_on")}</span>
              <button type="button" className={styles.platformBtn} aria-label="Share on X" onClick={shareOnX}>
                <XIcon size={20} />
              </button>
              <button type="button" className={styles.platformBtn} aria-label="Share on Discord" onClick={shareOnDiscord}>
                <DiscordIcon color="currentColor" size={24} />
              </button>
            </div>
            {(feedback === "discord" || feedback === "error") && (
              <p className={styles.shareFeedback} role="status">
                {feedback === "discord" ? t("share_discord_copied") : t("share_failed")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
